import React, { useState, useEffect } from 'react';
import { Link, NavLink } from "react-router-dom"; 
import { 
  GraduationCap, MapPin, Menu, X, Globe, Phone, Mail, 
  ChevronRight, Target, Users, Award, Heart, CheckCircle, 
  TrendingUp, Lightbulb, ShieldCheck, ArrowRight
} from 'lucide-react';

export default function AboutUsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Header Scroll Effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Team Data
  const team = [
    { name: "Dr. Rajesh Verma", role: "Founder & CEO", img: "11", bio: "Ex-IIT Delhi with 15+ years in Edu-tech." },
    { name: "Priya Malhotra", role: "Head of Counseling", img: "5", bio: "Certified Career Coach, guided 5000+ students." },
    { name: "Amit Kumar", role: "Chief Technology Officer", img: "3", bio: "Building our AI-driven college mapping engine." },
    { name: "Sarah Khan", role: "Study Abroad Lead", img: "9", bio: "Specialist in US & UK University admissions." }
  ];

  // Values Data
  const values = [
    { icon: <ShieldCheck size={32} />, title: "Transparency", desc: "No hidden fees, no bias. We recommend colleges that truly fit your profile, not just our partners." },
    { icon: <Users size={32} />, title: "Student First", desc: "Your career goals are our priority. We treat every student's journey as if it were our own." },
    { icon: <Lightbulb size={32} />, title: "Innovation", desc: "Using AI and data analytics to provide accurate admission probability and placement insights." },
    { icon: <Award size={32} />, title: "Excellence", desc: "We strive for 100% admission success rate for our premium counseling students." }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800 selection:bg-red-100 selection:text-red-900">
      
      {/* ================= HEADER (UPDATED WITH ACTIVE STATE) ================= */}
      <header 
        className={`fixed w-full z-50 transition-all duration-300 border-b border-transparent ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-slate-100 py-3' : 'bg-blue-950 py-5 border-blue-900'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            
            {/* LOGO */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <GraduationCap className={`w-10 h-10 ${isScrolled ? 'text-blue-950' : 'text-white'} transition-colors`} strokeWidth={2} />
                <div className="absolute top-[18px] left-1/2 -translate-x-1/2">
                  <MapPin className="w-4 h-4 text-red-600 fill-red-600" />
                </div>
              </div>

              <div className="flex flex-col">
                <span className={`text-2xl font-bold tracking-tight leading-none ${isScrolled ? 'text-blue-950' : 'text-white'}`}>
                  Map<span className={`${isScrolled ? 'text-blue-800' : 'text-blue-200'}`}>Your</span>College
                </span>
                <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase mt-1">
                  Nurturing Dreams, Creating Leaders
                </span>
              </div>
            </Link>

            {/* DESKTOP NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-8">
              {[
                { name: 'Home', path: '/' },
                { name: 'Colleges', path: '/colleges' },
                { name: 'About Us', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                <NavLink 
                  key={link.name}
                  to={link.path} 
                  className={({ isActive }) => 
                    `text-sm font-semibold transition-colors uppercase tracking-wide hover:text-red-600 ${
                      isActive 
                        ? 'text-red-600' // Active State Color
                        : (isScrolled ? 'text-slate-600' : 'text-blue-100') // Normal State
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              ))}

              <Link to="/contact">
                <button className="bg-red-600 text-white px-6 py-2.5 rounded text-sm font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 transform hover:-translate-y-0.5">
                  Get Started
                </button>
              </Link>
            </nav>

            {/* MOBILE MENU BUTTON */}
            <button 
              className={`${isScrolled ? 'text-blue-950' : 'text-white'} lg:hidden`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU DROPDOWN */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-xl lg:hidden p-4 flex flex-col gap-4 animate-in slide-in-from-top-2">
            {[
              { name: 'Home', path: '/' },
              { name: 'Colleges', path: '/colleges' },
              { name: 'About Us', path: '/about' },
              { name: 'Services', path: '/services' },
              { name: 'Contact', path: '/contact' }
            ].map((link) => (
              <NavLink 
                key={link.name}
                to={link.path} 
                className={({ isActive }) => 
                  `text-lg font-semibold py-2 border-b border-slate-50 ${
                    isActive ? 'text-red-600' : 'text-blue-950'
                  }`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </NavLink>
            ))}

            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              <button className="bg-red-600 text-white w-full py-3 rounded font-bold">Get Started</button>
            </Link>
          </div>
        )}
      </header>

      {/* ================= HERO SECTION ================= */}
      <section className="bg-blue-950 text-white pt-40 pb-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
           <span className="text-red-500 font-bold uppercase tracking-widest text-sm mb-4 block">Who We Are</span>
           <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
             Bridging the Gap Between <br /> <span className="text-blue-200">Ambition & Reality</span>
           </h1>
           <p className="text-lg md:text-xl text-blue-200 max-w-3xl mx-auto leading-relaxed">
             MapYourCollege is India's most trusted education consultancy, dedicated to helping students navigate the complex world of college admissions with data, empathy, and expertise.
           </p>
        </div>
      </section>

      {/* ================= OUR STORY ================= */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
             <div className="absolute -left-4 -top-4 w-24 h-24 bg-red-100 rounded-full z-0"></div>
             <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-blue-100 rounded-full z-0"></div>
             <img 
               src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800" 
               alt="Team Meeting" 
               className="relative z-10 rounded-2xl shadow-2xl border border-slate-100"
             />
          </div>
          
          <div>
            <h2 className="text-3xl font-extrabold text-blue-950 mb-6">Our Story</h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Founded in 2020, MapYourCollege began with a simple observation: Talent is universal, but opportunity is not. Millions of students in India miss out on their dream colleges simply due to a lack of awareness and guidance.
            </p>
            <p className="text-slate-600 mb-8 leading-relaxed">
              We started as a small team of 3 counselors in a small office. Today, we are a family of 50+ experts, having impacted the lives of over 10,000 students. Our philosophy remains unchanged: <strong>Nurturing Dreams, Creating Leaders.</strong>
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="border-l-4 border-red-600 pl-4">
                <h3 className="text-4xl font-bold text-blue-950">10k+</h3>
                <p className="text-sm text-slate-500 uppercase tracking-wide">Students Placed</p>
              </div>
              <div className="border-l-4 border-blue-600 pl-4">
                <h3 className="text-4xl font-bold text-blue-950">98%</h3>
                <p className="text-sm text-slate-500 uppercase tracking-wide">Success Rate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= MISSION & VISION ================= */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8">
           <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100 hover:-translate-y-2 transition-transform duration-300">
              <div className="w-16 h-16 bg-red-50 rounded-xl flex items-center justify-center mb-6">
                 <Target className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-blue-950 mb-4">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">
                To democratize access to quality higher education by providing affordable, accurate, and personalized counseling to every student, regardless of their background or location.
              </p>
           </div>

           <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100 hover:-translate-y-2 transition-transform duration-300">
              <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                 <TrendingUp className="w-8 h-8 text-blue-950" />
              </div>
              <h3 className="text-2xl font-bold text-blue-950 mb-4">Our Vision</h3>
              <p className="text-slate-600 leading-relaxed">
                To become India's most trusted education ecosystem where every student feels empowered to make informed career decisions and achieve their full potential.
              </p>
           </div>
        </div>
      </section>

      {/* ================= CORE VALUES ================= */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-wider text-sm">What Drives Us</span>
            <h2 className="text-3xl font-extrabold text-blue-950 mt-2">Our Core Values</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {values.map((val, idx) => (
              <div key={idx} className="text-center group p-6 rounded-xl hover:bg-slate-50 transition-colors">
                <div className="w-16 h-16 mx-auto bg-blue-50 rounded-full flex items-center justify-center text-blue-950 mb-6 group-hover:bg-blue-950 group-hover:text-white transition-colors duration-300 shadow-lg">
                  {val.icon}
                </div>
                <h4 className="text-xl font-bold text-blue-950 mb-3">{val.title}</h4>
                <p className="text-sm text-slate-500 leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TEAM SECTION ================= */}
      <section className="py-24 px-4 bg-blue-950 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600 rounded-full blur-3xl opacity-10 translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-white">Meet the Mentors</h2>
            <p className="text-blue-200 mt-4 max-w-2xl mx-auto">The experienced minds behind your success stories.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {team.map((member, idx) => (
              <div key={idx} className="group relative">
                <div className="bg-white rounded-xl overflow-hidden shadow-2xl transform transition-all duration-300 group-hover:-translate-y-2">
                  <div className="h-64 bg-slate-200 overflow-hidden">
                    <img 
                      src={`https://i.pravatar.cc/300?img=${member.img}`} 
                      alt={member.name} 
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h4 className="text-lg font-bold text-blue-950">{member.name}</h4>
                    <p className="text-red-600 text-xs font-bold uppercase tracking-wider mb-3">{member.role}</p>
                    <p className="text-xs text-slate-500">{member.bio}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="py-20 px-4 bg-white">
         <div className="max-w-5xl mx-auto bg-slate-50 border border-slate-100 rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-950 via-red-600 to-blue-950"></div>
            <h2 className="text-3xl font-bold text-blue-950 mb-6">Ready to Plan Your Future?</h2>
            <p className="text-slate-600 max-w-xl mx-auto mb-8">Join thousands of students who have found their dream colleges through MapYourCollege. Let's start your journey today.</p>
            <div className="flex justify-center gap-4">
               <button className="bg-blue-950 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-900 transition-colors shadow-lg">
                  Contact Us
               </button>
               <button className="bg-white text-blue-950 border border-blue-950 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors">
                  View Success Stories
               </button>
            </div>
         </div>
      </section>

      {/* ================= FOOTER ================= */}


    </div>
  );
}